import { Component, type ErrorInfo, type ReactNode } from "react";
import { DefaultErrorFallback } from "@/components/feedback/DefaultErrorFallback";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback; defaults to the built-in error screen. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches errors thrown during rendering anywhere in its subtree.
 * React error boundaries must be class components — there is no hook
 * equivalent to componentDidCatch / getDerivedStateFromError as of React 18.
 *
 * This does NOT catch errors in event handlers, async code, or effects —
 * those are handled locally (try/catch + state) where they occur, e.g. in
 * AuthContext's loadUserData.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Centralised place to wire in remote error reporting later
    // (Constitution Article VII — auditability of important failures).
    console.error("Unhandled UI error:", error, info.componentStack);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset);
    }

    return <DefaultErrorFallback error={error} onRetry={this.reset} />;
  }
}
